import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAraTheme } from '../contexts/AraThemeContext';
import { setAraAppState } from '../utils/ara-storage';
import { ArrowRight, ArrowLeft, Check } from 'phosphor-react';

/*
 * Ara onboarding — supporter-specific. Collects preferred name, reason for
 * visiting (one of 7 pathways), optional support context. Everything is
 * optional and user-skippable. Preferences saved in AraSanctuaryDB (local).
 */

const PATHWAYS = [
  { id: 'supporting_someone', label: "I'm supporting someone through a difficult period" },
  { id: 'challenging_relationship', label: "I'm navigating a challenging relationship" },
  { id: 'communicate_compassionately', label: "I want to communicate more compassionately" },
  { id: 'understand_reactions', label: "I'm struggling to understand someone else's reactions" },
  { id: 'own_needs', label: "I need help recognizing and expressing my own needs" },
  { id: 'own_reflection', label: "I'm here for my own reflection and support" },
  { id: 'just_talk', label: "I'd rather just start talking" },
];

const AraOnboarding = () => {
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [pathway, setPathway] = useState('');
  const [context, setContext] = useState('');
  const { theme } = useAraTheme();
  const navigate = useNavigate();
  const logo = theme === 'dark' ? '/ara-logo-dark.png' : '/ara-logo-light.png';

  const finish = async (skipAll = false) => {
    try {
      if (!skipAll) {
        if (name.trim()) await setAraAppState('ara_name', name.trim());
        if (pathway) await setAraAppState('ara_pathway', pathway);
        if (context.trim()) await setAraAppState('ara_context', context.trim());
      }
      await setAraAppState('ara_onboarding_completed', true);
    } catch (e) {
      // non-fatal — still proceed
    }
    navigate('/ara/chat');
  };

  const Step = ({ title, subtitle, children, canSkip = true, onNext, nextLabel = 'Continue' }) => (
    <div className="ara-card rounded-3xl p-6 sm:p-8">
      <h2 className="text-2xl font-serif font-semibold ara-text mb-2">{title}</h2>
      {subtitle && <p className="text-sm ara-text-muted mb-6">{subtitle}</p>}
      <div className="mb-6">{children}</div>
      <div className="flex items-center justify-between gap-3">
        {canSkip ? (
          <button
            onClick={() => (step === 2 ? finish(false) : setStep(step + 1))}
            className="ara-cta-ghost rounded-full px-4 py-2 text-sm"
            data-testid={`ara-onb-skip-${step}`}
          >
            Skip
          </button>
        ) : <span />}
        <button
          onClick={onNext}
          className="ara-cta-primary inline-flex items-center gap-2 rounded-full px-6 py-3 font-medium"
          data-testid={`ara-onb-next-${step}`}
        >
          {nextLabel}
          {nextLabel !== 'Enter Ara' && <ArrowRight size={16} weight="bold" />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="ara-root min-h-screen" data-ara-theme={theme} data-testid="ara-onboarding">
      <header className="max-w-3xl mx-auto w-full px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Ara" className="w-10 h-10 rounded-full" />
          <span className="font-serif font-semibold ara-text">Ara</span>
        </div>
        {step > 0 && (
          <button
            onClick={() => setStep(step - 1)}
            className="inline-flex items-center gap-2 ara-text-muted hover:ara-text transition-colors text-sm"
            data-testid="ara-onb-prev"
          >
            <ArrowLeft size={16} /> Back
          </button>
        )}
      </header>

      <main className="max-w-xl mx-auto px-6 pt-4 pb-16">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-6">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-all ${i <= step ? 'ara-progress-on' : 'ara-progress-off'}`}
            />
          ))}
        </div>

        {step === 0 && (
          <Step
            title="What may I call you?"
            subtitle="This stays on your device. Totally optional."
            onNext={() => setStep(1)}
          >
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="A name or nickname"
              className="ara-input w-full rounded-xl px-4 py-3"
              data-testid="ara-onb-name-input"
              maxLength={60}
            />
          </Step>
        )}

        {step === 1 && (
          <Step
            title={name ? `Welcome, ${name}. What brings you to Ara?` : 'What brings you to Ara?'}
            subtitle="You can pick what fits closest, or skip. You can change this later."
            onNext={() => setStep(2)}
          >
            <div className="space-y-2">
              {PATHWAYS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPathway(p.id)}
                  className={`w-full text-left rounded-xl px-4 py-3 border transition-all flex items-start gap-3 ${
                    pathway === p.id ? 'ara-option-selected' : 'ara-option'
                  }`}
                  data-testid={`ara-onb-pathway-${p.id}`}
                >
                  <span className="mt-0.5 flex-shrink-0 w-4 h-4 rounded-full ara-option-dot">
                    {pathway === p.id && <Check size={12} weight="bold" />}
                  </span>
                  <span className="text-sm">{p.label}</span>
                </button>
              ))}
            </div>
          </Step>
        )}

        {step === 2 && (
          <Step
            title="Anything you'd like Ara to hold in mind?"
            subtitle="A sentence or two about your situation — only what you want to share. Stays private, on this device."
            onNext={() => finish(false)}
            nextLabel="Enter Ara"
          >
            <textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="For example: I'm supporting my sister after a hard loss and I'm feeling stretched thin."
              className="ara-input w-full rounded-xl px-4 py-3 min-h-[120px] resize-none"
              data-testid="ara-onb-context-input"
              maxLength={500}
            />
            <p className="text-xs ara-text-muted mt-2">
              Ara will not retain details about anyone else beyond what you choose
              to say. You can clear this anytime.
            </p>
          </Step>
        )}

        <div className="text-center mt-6">
          <button
            onClick={() => finish(true)}
            className="text-xs ara-text-muted hover:ara-text transition-colors"
            data-testid="ara-onb-skip-all"
          >
            Skip all and start talking
          </button>
        </div>
      </main>
    </div>
  );
};

export default AraOnboarding;
